import type { AlgoliaRefinementState, ExtraFilters } from '../types';

/**
 * Extracts user-applied refinements from Algolia's SearchParameters state
 * and returns an Algolia-format filter string, or undefined if none.
 *
 * This excludes the base `filters` string (e.g. "price > 0 AND product_published = 1")
 * which is set via <Configure> and is NOT a user-applied filter.
 */
const escapeFilterValue = (v: string) => v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const buildAlgoliaUserFilter = (state: AlgoliaRefinementState | undefined, extra?: ExtraFilters) => {
  const parts: Array<string> = [];

  if (state?.disjunctiveFacetsRefinements) {
    for (const [key, values] of Object.entries(state.disjunctiveFacetsRefinements)) {
      if (values?.length) {
        const group = values.map(v => `${key}:"${escapeFilterValue(v)}"`).join(' OR ');
        parts.push(values.length > 1 ? `(${group})` : group);
      }
    }
  }

  if (state?.facetsRefinements) {
    for (const [key, values] of Object.entries(state.facetsRefinements)) {
      if (values?.length) {
        values.forEach(v => parts.push(`${key}:"${escapeFilterValue(v)}"`));
      }
    }
  }

  if (state?.numericRefinements) {
    for (const [key, operators] of Object.entries(state.numericRefinements)) {
      if (!operators || typeof operators !== 'object') continue;
      const min = operators['>=']?.[0] as number | undefined;
      const max = operators['<=']?.[0] as number | undefined;
      if (min != null && min > 0) parts.push(`${key} >= ${min}`);
      if (max != null && max > 0) parts.push(`${key} <= ${max}`);
    }
  }

  if (extra?.priceRange?.min) parts.push(`price >= ${extra.priceRange.min}`);
  if (extra?.priceRange?.max) parts.push(`price <= ${extra.priceRange.max}`);
  if (extra?.saleItems) parts.push('onPromotion:"true"');

  return parts.length > 0 ? parts.join(' AND ') : undefined;
};
